/**
 * Electric Picks — Collaborations nav link click tracking
 * ---------------------------------------------------------------------------
 * Pushes a `collab_nav_click` data layer event when a visitor clicks the
 * top-level COLLABORATIONS item in the main navigation. SEO/analytics build the
 * GA4 tag in GTM off that event name.
 *
 * Why a storefront push instead of a GTM CSS-selector click trigger: the GTM
 * container runs inside Shopify's sandboxed Custom Pixel, so it cannot attach
 * click triggers to theme elements or read the theme's window.dataLayer.
 *
 * The event is broadcast two ways:
 *   1. Shopify.analytics.publish() -- the supported route into the pixel
 *      sandbox. The Elevar Custom Pixel (Settings > Customer Events) subscribes
 *      with analytics.subscribe("collab_nav_click", ...) and pushes to the
 *      sandbox's own window.dataLayer, where GTM can see it. The payload
 *      arrives on the subscriber as event.customData (NOT event.data).
 *   2. window.dataLayer -- for any container loaded directly in the theme
 *      (the GTM-TBHMJ6B5 noscript in layout/theme.liquid).
 *
 * Matching — the nav is rendered from a Shopify linklist, so there is no
 * theme-side hook to hang a data attribute on. Two signals are used because the
 * same menu item renders differently per breakpoint:
 *   desktop (snippets/desktop-navbar.liquid)   href="/collections/all-collabs"
 *   mobile  (snippets/mobile-nav-drawer.liquid) data-href="#" + submenu toggle
 * So: match the collabs URL, OR the link title, scoped to the main nav. Menu
 * changes in Shopify admin (retitling, repointing) need this list updated.
 */
(function () {
  if (window.__epCollabNavTrackingInit) return;
  window.__epCollabNavTrackingInit = true;

  var COLLAB_PATH = '/collections/all-collabs';
  var COLLAB_TITLE = 'collaborations';
  // How long to hold desktop navigation so the sandbox receives the event.
  var NAV_HOLD_MS = 150;

  window.dataLayer = window.dataLayer || [];

  function broadcast(payload) {
    // Container loaded in the theme context.
    try { window.dataLayer.push(payload); } catch (e) {}

    // Into the Custom Pixel sandbox. Only the payload object crosses that
    // boundary; the subscriber reads it off event.customData.
    try {
      if (typeof Shopify !== "undefined" && Shopify.analytics &&
          typeof Shopify.analytics.publish === "function") {
        Shopify.analytics.publish("collab_nav_click", payload.collab_nav);
      }
    } catch (e) {}
  }

  // Trailing-slash-insensitive path compare, tolerant of locale prefixes
  // (/en-us/collections/all-collabs) and of href being absolute or relative.
  function isCollabHref(href) {
    if (!href || href === '#') return false;
    var path;
    try {
      path = new URL(href, window.location.origin).pathname;
    } catch (e) {
      path = href;
    }
    path = path.replace(/\/+$/, '').toLowerCase();
    if (path === COLLAB_PATH) return true;
    // Locale-prefixed route, e.g. /en-us/collections/all-collabs
    return path.length > COLLAB_PATH.length &&
      path.slice(-COLLAB_PATH.length) === COLLAB_PATH;
  }

  function linkTitle(el) {
    var title = el.getAttribute('title') || el.getAttribute('name') || el.textContent || '';
    return title.trim().toLowerCase();
  }

  function navLocation(el) {
    return el.closest('#mobile-menu-drawer') ? 'mobile_drawer' : 'desktop_navbar';
  }

  // Returns the collabs nav link for this event, or null.
  function matchedLink(event) {
    var el = event.target && event.target.closest ? event.target.closest('a') : null;
    if (!el) return null;

    // Main nav only — keeps footer/body links to the same collection out of it.
    if (!el.closest('.navbar-nav')) return null;

    // Top-level item only. Child links inside an open dropdown/megamenu sit in
    // a .dropdown-menu / submenu and are not what SEO asked to measure.
    if (el.closest('.dropdown-menu') || el.closest('.megamenu-v2-dropdown')) return null;

    var href = el.getAttribute('href') || el.getAttribute('data-href');
    if (!isCollabHref(href) && linkTitle(el) !== COLLAB_TITLE) return null;

    return el;
  }

  function handleClick(event) {
    var el = matchedLink(event);
    if (!el) return;

    var href = el.getAttribute('href') || el.getAttribute('data-href');

    broadcast({
      event: 'collab_nav_click',
      collab_nav: {
        link_text: (el.textContent || '').trim(),
        link_url: isCollabHref(href) ? href : COLLAB_PATH,
        nav_location: navLocation(el),        // "desktop_navbar" | "mobile_drawer"
        page_path: (window.location && window.location.pathname) || ''
      }
    });
  }

  // Capture phase: the dropdown toggle calls stopPropagation on some
  // breakpoints, and the desktop link navigates away immediately.
  document.addEventListener('click', handleClick, true);

  // On desktop this link navigates. Shopify.analytics.publish() posts a message
  // into the pixel sandbox, and the page can unload before the sandbox handles
  // it — so desktop clicks were dropped while mobile (a submenu toggle, no
  // navigation) reported fine. Hold navigation briefly so the message lands.
  //
  // Bound directly to the anchor, NOT delegated from document: the dropdown
  // component calls stopPropagation() on this link, so a document-level bubble
  // listener never runs. Listeners on the element itself still fire.
  function deferNavigation(event) {
    // The dropdown may have cancelled navigation to open the megamenu instead.
    // Nothing to hold in that case.
    if (event.defaultPrevented) return;

    // Let the browser handle anything that will not unload this page:
    // middle/right click, and modifier clicks that open a new tab or window.
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var el = event.currentTarget;
    if (!el || (el.target && el.target !== '_self')) return;

    var url = el.href;
    if (!url) return;

    event.preventDefault();

    // Hard cap: navigation always happens, even if anything above misbehaves.
    var navigated = false;
    setTimeout(function () {
      if (navigated) return;
      navigated = true;
      window.location.href = url;
    }, NAV_HOLD_MS);
  }

  // Only real navigating links need this. The mobile toggle has data-href='#'
  // and never navigates, so it is skipped by the isCollabHref test.
  function bindDeferral() {
    var links = document.querySelectorAll('.navbar-nav a[href]');
    for (var i = 0; i < links.length; i++) {
      var el = links[i];
      if (el.__epNavDeferBound) continue;
      if (!isCollabHref(el.getAttribute('href'))) continue;
      if (el.closest('.dropdown-menu') || el.closest('.megamenu-v2-dropdown')) continue;
      el.__epNavDeferBound = true;
      el.addEventListener('click', deferNavigation, false);
    }
  }

  bindDeferral();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindDeferral);
  }
  // Theme editor re-renders the header section.
  document.addEventListener('shopify:section:load', bindDeferral);
})();
